<template>
  <section class="qr-account" aria-label="Pago de cuenta Redsys" :aria-busy="busy">
    <p role="status">{{ message }}</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <template v-if="pending">
      <p>Importe solicitado: {{ money(pending.amount_cents + pending.tip_cents) }}.</p>
      <button type="button" :disabled="busy" @click="recover">Consultar pago pendiente</button>
      <button v-if="missing && pending.operation_epoch && context?.enabled && context.can_pay" type="button" :disabled="busy" @click="retryPreparation">Reintentar preparación del mismo pago</button>
      <button v-if="completed" type="button" :disabled="busy" @click="acknowledge">Actualizar cuenta</button>
      <button v-if="resumeId && !completed" type="button" :disabled="busy" @click="resumePayment">Continuar el mismo pago</button>
    </template>
    <button v-else-if="context?.enabled" type="button" :disabled="busy || !initialized || !context.can_pay || amount <= 0" @click="start">Pagar {{ money(Math.round((amount + tip) * 100)) }} con {{ provider }}</button>
    <iframe v-if="hostedUrl" :src="hostedUrl" title="Pago seguro Dojo" allow="payment"></iframe>
    <template v-if="form">
      <form ref="gateway" :action="form.action" method="POST" target="coordinatedRedsysFrame">
        <input v-for="name in ['Ds_SignatureVersion','Ds_MerchantParameters','Ds_Signature']" :key="name" type="hidden" :name="name" :value="form[name]">
      </form>
      <iframe name="coordinatedRedsysFrame" title="Pago seguro Redsys" allow="payment"></iframe>
    </template>
  </section>
</template>

<script>
import axios from 'axios'
export default {
  props: { token: String, backend: String, amount: Number, tip: Number, provider: { type: String, default: 'redsys' } },
  emits: ['ready', 'balance', 'resolved'],
  data: () => ({ context: null, pending: null, completed: null, form: null,
    busy: false, initialized: false, missing: false, resumeId: null, hostedUrl: null, error: '', message: 'Comprobando la cuenta…', generation: 0, key: null }),
  mounted() { this.initialize(); window.addEventListener('storage', this.storageChanged) },
  beforeUnmount() { this.generation++; window.removeEventListener('storage', this.storageChanged) },
  watch: { token() { this.initialize() }, backend() { this.initialize() }, provider() { this.initialize() } },
  methods: {
    money(cents) { return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(cents / 100) },
    async request(method, path, params) {
      const token = this.token, backend = this.backend, generation = this.generation
      const response = await axios({ method, url: `${backend}/app/payments/${this.provider === 'redsys' ? 'redsys' : 'hosted'}/${path}`, params: { ...params, provider: this.provider }, timeout: 30000,
        validateStatus: status => status === 200 || (method === 'get' && path === 'status' && status === 404),
        headers: { Authorization: `Bearer ${token}`,
          ...(params?.operation_epoch ? { 'X-EWU-Operation-Epoch': String(params.operation_epoch) } : {}) } })
      if (generation !== this.generation || token !== this.token || backend !== this.backend) throw Error('SESSION_CHANGED')
      if (response.status === 404) {
        if (response.data?.code !== (this.provider === 'redsys' ? 'REDSYS_TRANSACTION_NOT_FOUND' : 'HOSTED_TRANSACTION_NOT_FOUND')) throw Error('UNVERIFIED_MISSING')
        return { missing: true }
      }
      return response.data
    },
    read() {
      const raw = localStorage.getItem(this.key)
      if (!raw) return null
      const value = JSON.parse(raw)
      if (!/^[0-9a-f-]{36}$/i.test(value.attempt_id) || !Number.isSafeInteger(value.amount_cents)
          || !Number.isSafeInteger(value.tip_cents) || value.amount_cents <= 0 || value.tip_cents < 0) throw Error('INVALID_STORAGE')
      return value
    },
    save(value) {
      const raw = JSON.stringify(value)
      localStorage.setItem(this.key, raw)
      if (localStorage.getItem(this.key) !== raw) throw Error('STORAGE_UNAVAILABLE')
      this.pending = value
    },
    async run(action) {
      if (this.busy) return
      this.busy = true; this.error = ''; const generation = this.generation
      try { await action() } catch {
        if (generation === this.generation) this.error = 'No se pudo confirmar el pago. Consulta su estado antes de volver a pagar.'
      } finally { if (generation === this.generation) this.busy = false }
    },
    async initialize() {
      this.generation++; this.busy = false; this.context = null; this.pending = null; this.form = null; this.completed = null
      this.initialized = false
      this.resumeId = null; this.hostedUrl = null
      this.missing = false
      this.$emit('ready', { enabled: true })
      await this.run(async () => {
        this.context = await this.request('get', 'account-context')
        const identity = [this.backend, this.context.restaurant_id, this.context.token_id]
        if (this.provider !== 'redsys') identity.push(this.provider)
        this.key = `ewu.qr-account.v1:${JSON.stringify(identity)}`
        this.pending = this.read()
        if (!this.pending && this.context.pending_id) this.save({ attempt_id: this.context.pending_id,
          amount_cents: this.context.amount_cents, tip_cents: this.context.tip_cents })
        this.initialized = true
        this.$emit('ready', { enabled: this.context.enabled || !!this.pending })
        if (this.pending) { this.message = 'Hay un pago pendiente. Consulta su resultado; no vuelvas a pagar.'; return }
        if (this.context.enabled) {
          this.$emit('balance', this.context.outstanding_cents)
          this.message = this.context.account_error || (this.context.can_pay
            ? 'El importe se comprueba con el saldo actual del restaurante.'
            : 'El restaurante no permite iniciar un pago en este momento.')
        } else this.message = ''
      })
    },
    async start() {
      if (!this.initialized || !this.context?.can_pay || this.pending || this.busy) return
      await this.run(async () => {
        if (!navigator.locks) throw Error('LOCK_UNAVAILABLE')
        const generation = this.generation
        await navigator.locks.request(this.key, async () => {
          if (generation !== this.generation) throw Error('SESSION_CHANGED')
          this.pending = this.read()
          if (this.pending) return
          const amount = Math.round(this.amount * 100), tip = Math.round(this.tip * 100)
          if (!Number.isSafeInteger(amount) || amount <= 0 || !Number.isSafeInteger(tip) || tip < 0) throw Error('INVALID_AMOUNT')
          this.save({ attempt_id: crypto.randomUUID(), amount_cents: amount, tip_cents: tip, operation_epoch: this.context.operation_epoch })
          await this.dispatchPending(generation)
        })
      })
    },
    async dispatchPending(generation) {
      const pending = this.pending
      const data = await this.request('post', 'session', { amount: (pending.amount_cents / 100).toFixed(2), tip: (pending.tip_cents / 100).toFixed(2),
        cart_payment: false, attempt_id: pending.attempt_id, operation_epoch: pending.operation_epoch })
      if (data.transaction_id !== pending.attempt_id) throw Error('FOREIGN_PAYMENT')
      if (this.provider !== 'redsys') {
        if (data.provider !== this.provider || data.amount_cents !== pending.amount_cents + pending.tip_cents) throw Error('INVALID_PAYMENT')
        if (data.payment_id) this.launchHosted(data.payment_id)
        else this.message = 'Solicitud ya enviada. Consulta su resultado.'
        return
      }
      if (!data.action) { this.message = 'Solicitud ya enviada. Consulta su resultado.'; return }
      if (data.amount_cents !== pending.amount_cents + pending.tip_cents || new URL(data.action).protocol !== 'https:') throw Error('INVALID_FORM')
      this.form = data
      await this.$nextTick()
      if (generation !== this.generation) throw Error('SESSION_CHANGED')
      this.$refs.gateway.submit()
      this.message = 'Completa el pago en Redsys y consulta el resultado. Cerrar esta ventana no cancela el pago.'
    },
    async retryPreparation() {
      if (!this.missing || !this.pending?.operation_epoch || !this.context?.enabled || !this.context.can_pay) return
      await this.run(async () => {
        const generation = this.generation, identifier = this.pending.attempt_id
        if (!navigator.locks) throw Error('LOCK_UNAVAILABLE')
        await navigator.locks.request(this.key, async () => {
          if (generation !== this.generation) throw Error('SESSION_CHANGED')
          this.pending = this.read()
          if (this.pending?.attempt_id !== identifier) throw Error('CHANGED_INTENT')
          this.missing = false
          const result = await this.request('get', 'status', { transaction_id: identifier })
          if (!result.missing) { this.message = 'La solicitud ya existe. Consulta su resultado.'; return }
          await this.dispatchPending(generation)
        })
      })
    },
    async recover() {
      if (!this.pending) return
      await this.run(async () => {
        this.missing = false
        this.resumeId = null
        const data = await this.request('get', 'status', { transaction_id: this.pending.attempt_id })
        if (data.missing) { this.missing = true; this.message = 'El servidor no tiene esta solicitud. Puedes reintentar su preparación conservando el mismo identificador.'; return }
        if (!data.coordinated || data.transaction_id !== this.pending.attempt_id) throw Error('FOREIGN_PAYMENT')
        if (this.provider !== 'redsys') {
          if (data.provider !== this.provider || data.amount_cents !== this.pending.amount_cents + this.pending.tip_cents) throw Error('FOREIGN_PAYMENT')
          this.resumeId = data.payment_id || null
        }
        if (data.status === 'authorized' && data.state === 'settled') {
          this.completed = data; this.message = 'Pago registrado. Actualiza la cuenta para continuar.'
        } else if (data.status === 'failed' && ['no_payment', 'cancelled'].includes(data.state) && !data.payment_confirmed) {
          this.completed = data; this.hostedUrl = null; this.form = null
          this.message = 'Se ha confirmado la cancelación sin cobro. Actualiza la cuenta para continuar.'
        } else this.message = data.payment_confirmed
          ? 'El dinero está confirmado y falta actualizar la cuenta. No vuelvas a pagar.'
          : 'El pago todavía no tiene un resultado definitivo. Consulta de nuevo o avisa al personal.'
      })
    },
    launchHosted(identifier) {
      if (!/^[A-Za-z0-9_-]{1,100}$/.test(identifier)) throw Error('INVALID_REMOTE_ID')
      this.resumeId = null
      if (this.provider === 'dojo') this.hostedUrl = `https://pay.dojo.tech/checkout/${identifier}`
      else if (this.provider === 'monei') {
        if (!window.monei?.confirmPayment) throw Error('MONEI_UNAVAILABLE')
        const generation = this.generation
        Promise.resolve(window.monei.confirmPayment({ paymentId: identifier }))
          .then(() => { if (generation === this.generation) this.recover() })
          .catch(() => { if (generation === this.generation) this.message = 'Consulta el resultado del pago antes de volver a intentarlo.' })
      } else throw Error('INVALID_PROVIDER')
      this.message = 'Completa el pago y consulta su resultado. Cerrar esta ventana no cancela el pago.'
    },
    async resumePayment() {
      if (!this.resumeId || this.busy) return
      // Verify again immediately before opening the previously created checkout.
      await this.run(async () => {
        const data = await this.request('get', 'status', { transaction_id: this.pending.attempt_id })
        if (!data.coordinated || data.transaction_id !== this.pending.attempt_id || data.provider !== this.provider
            || data.amount_cents !== this.pending.amount_cents + this.pending.tip_cents || !data.payment_id) {
          this.resumeId = null; this.message = 'El estado ha cambiado. Consulta el resultado.'; return
        }
        this.launchHosted(data.payment_id)
      })
    },
    async acknowledge() {
      if (!this.completed || !this.pending || this.busy) return
      await this.run(async () => {
        const result = this.completed
        localStorage.removeItem(this.key)
        if (localStorage.getItem(this.key) !== null) throw Error('STORAGE_UNAVAILABLE')
        this.$emit('resolved', result)
      })
    },
    storageChanged(event) { if (event.key === this.key || event.key === null) this.initialize() },
  },
}
</script>

<style scoped>
.qr-account { padding: 12px; border: 1px solid #d1d5db; border-radius: 8px; margin: 12px 0; }
.qr-account button { padding: 10px; margin: 4px; }
.qr-account iframe { width: 100%; min-height: 460px; border: 0; }
.qr-account [role="alert"] { color: #9a3412; }
</style>
